"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.isLikelyCadFile = isLikelyCadFile;
exports.validateFile = validateFile;
exports.getSupportedFileTypes = getSupportedFileTypes;
exports.validateFileType = validateFileType;
exports.validateFileExtensionSupported = validateFileExtensionSupported;
exports.detectProjectsForTool = detectProjectsForTool;
const altiumValidation_1 = require("./Altium/altiumValidation");
const altiumFileTypes_1 = require("./Altium/altiumFileTypes");
const kicadValidation_1 = require("./Kicad/kicadValidation");
const kicad_file_type_1 = require("./Kicad/kicad-file-type");
const eagleValidation_1 = require("./Eagle/eagleValidation");
const orcadValidation_1 = require("./OrCAD/orcadValidation");
const allegroValidation_1 = require("./Allegro/allegroValidation");
const xpeditionValidation_1 = require("./Xpedition/xpeditionValidation");
const TEXT_CAD_EXTENSIONS = new Set([
    '.kicad_pcb',
    '.kicad_sch',
    '.sch',
    '.lib',
    '.brd',
    '.dcm',
    '.kicad_pro',
    '.kicad_prl',
    '.net',
    '.lbr',
    '.dru',
    '.opj',
    '.dsn',
    '.xml',
    '.onl',
]);
const TEXT_CAD_EXACT_FILENAMES = new Set(['fp-lib-table', 'sym-lib-table']);
function couldBeTextCadFile(filename) {
    const lower = filename.toLowerCase();
    const basename = lower.includes('/') ? lower.slice(lower.lastIndexOf('/') + 1) : lower;
    if (TEXT_CAD_EXACT_FILENAMES.has(basename)) {
        return true;
    }
    const dotIndex = basename.lastIndexOf('.');
    return dotIndex !== -1 && TEXT_CAD_EXTENSIONS.has(basename.slice(dotIndex));
}
function isOrCadDsnFile(filename) {
    return filename.toLowerCase().endsWith(orcadValidation_1.OrCADXFileType.OrCADCaptureSchematic);
}
function isLikelyCadFile(filename) {
    return (!!(0, altiumValidation_1.getAltiumFileType)(filename) ||
        !!(0, allegroValidation_1.getAllegroFileType)(filename) ||
        !!(0, xpeditionValidation_1.getXpeditionFileType)(filename) ||
        couldBeTextCadFile(filename));
}
function withClaimedTools(result, claimedTools) {
    if (result.isValid || claimedTools.length === 0) {
        return result;
    }
    return { ...result, claimedTools: [...new Set(claimedTools)] };
}
async function validateFile(filename, fileContents) {
    if (!filename || filename.length <= 1 || !fileContents) {
        return {
            isValid: false,
            data: undefined,
            errors: [`Invalid file name or contents ${filename}`],
        };
    }
    const claimedTools = [];
    let lastValidationResult;
    if ((0, altiumValidation_1.getAltiumFileType)(filename)) {
        claimedTools.push('altium');
        lastValidationResult = await (0, altiumValidation_1.validateAltiumFile)(filename, fileContents);
        if (lastValidationResult.isValid) {
            return lastValidationResult;
        }
    }
    if ((0, xpeditionValidation_1.getXpeditionFileType)(filename)) {
        return (0, xpeditionValidation_1.validateXpeditionFile)(filename);
    }
    if (isOrCadDsnFile(filename)) {
        claimedTools.push('orcad');
        return withClaimedTools(await (0, orcadValidation_1.validateOrCADXFile)(filename, new Uint8Array(await fileContents.arrayBuffer())), claimedTools);
    }
    if ((0, allegroValidation_1.getAllegroFileType)(filename) === allegroValidation_1.AllegroFileType.Cdsz) {
        claimedTools.push('allegro');
        return withClaimedTools((0, allegroValidation_1.validateAllegroFile)(filename, new Uint8Array(await fileContents.arrayBuffer())), claimedTools);
    }
    if (!couldBeTextCadFile(filename) && !(0, allegroValidation_1.getAllegroFileType)(filename)) {
        return withClaimedTools(lastValidationResult ?? {
            isValid: false,
            data: undefined,
            errors: [`Unrecognized file type ${filename}`],
        }, claimedTools);
    }
    const textContents = await fileContents.text();
    if ((0, kicadValidation_1.getKicadFileType)(filename, textContents)) {
        claimedTools.push('kicad');
        lastValidationResult = (0, kicadValidation_1.validateKicadFile)(filename, textContents);
        if (lastValidationResult.isValid) {
            return lastValidationResult;
        }
    }
    if ((0, orcadValidation_1.getOrCADXFileType)(filename, textContents)) {
        claimedTools.push('orcad');
        lastValidationResult = await (0, orcadValidation_1.validateOrCADXFile)(filename, textContents);
        if (lastValidationResult.isValid) {
            return lastValidationResult;
        }
    }
    if ((0, eagleValidation_1.getEagleFileType)(filename, textContents)) {
        claimedTools.push('eagle');
        lastValidationResult = (0, eagleValidation_1.validateEagleFile)(filename, textContents);
        if (lastValidationResult.isValid) {
            return lastValidationResult;
        }
    }
    if ((0, allegroValidation_1.getAllegroFileType)(filename, textContents)) {
        claimedTools.push('allegro');
        lastValidationResult = (0, allegroValidation_1.validateAllegroFile)(filename, textContents);
        if (lastValidationResult.isValid) {
            return lastValidationResult;
        }
    }
    return withClaimedTools(lastValidationResult ?? {
        isValid: false,
        data: undefined,
        errors: [`Unrecognized file type ${filename}`],
    }, claimedTools);
}
function getSupportedFileTypes() {
    return {
        kicad: [
            kicad_file_type_1.KicadFileType.EESchemaBrd,
            kicad_file_type_1.KicadFileType.KicadPcb,
            kicad_file_type_1.KicadFileType.KicadSch,
            kicad_file_type_1.KicadFileType.EESchemaSch,
            kicad_file_type_1.KicadFileType.EESchemaLib,
            kicad_file_type_1.KicadFileType.KicadFootprintLibTable,
            kicad_file_type_1.KicadFileType.KicadSymbolLibTable,
            kicad_file_type_1.KicadFileType.KicadDocLib,
            kicad_file_type_1.KicadFileType.KicadProject,
            kicad_file_type_1.KicadFileType.KicadProjectLocalSettings,
        ],
        eagle: [eagleValidation_1.EagleFileType.EagleBrd, eagleValidation_1.EagleFileType.EagleSch, eagleValidation_1.EagleFileType.EagleLbr, eagleValidation_1.EagleFileType.EagleDru],
        altium: [
            altiumFileTypes_1.AltiumFileType.Sch,
            altiumFileTypes_1.AltiumFileType.SchDoc,
            altiumFileTypes_1.AltiumFileType.PcbDoc,
            altiumFileTypes_1.AltiumFileType.Project,
            altiumFileTypes_1.AltiumFileType.ProjectStructure,
            altiumFileTypes_1.AltiumFileType.ProjectVariants,
            altiumFileTypes_1.AltiumFileType.OutJob,
            altiumFileTypes_1.AltiumFileType.PcbDrawing,
            altiumFileTypes_1.AltiumFileType.Harness,
            altiumFileTypes_1.AltiumFileType.BomDoc,
            altiumFileTypes_1.AltiumFileType.Stackup,
            altiumFileTypes_1.AltiumFileType.Netlist,
        ],
    };
}
async function validateFileType(filename, fileContents) {
    if (!filename || filename.length <= 1 || !fileContents) {
        return false;
    }
    if ((0, altiumValidation_1.getAltiumFileType)(filename)) {
        return true;
    }
    if (isOrCadDsnFile(filename)) {
        return true;
    }
    if ((0, xpeditionValidation_1.getXpeditionFileType)(filename)) {
        return true;
    }
    if ((0, allegroValidation_1.getAllegroFileType)(filename)) {
        return true;
    }
    if (!couldBeTextCadFile(filename)) {
        return false;
    }
    const textContents = await fileContents.text();
    if ((0, kicadValidation_1.getKicadFileType)(filename, textContents)) {
        return true;
    }
    if ((0, orcadValidation_1.getOrCADXFileType)(filename, textContents)) {
        return true;
    }
    if ((0, eagleValidation_1.getEagleFileType)(filename, textContents)) {
        return true;
    }
    return false;
}
function validateFileExtensionSupported(filename, allowedExtensions) {
    if (!filename) {
        return false;
    }
    filename = filename.toLowerCase();
    return allowedExtensions.some((e) => e.includes('.') ? filename.endsWith(e.toLowerCase()) : filename === e.toLowerCase());
}
function detectProjectsForTool(tool, files, fileDetails) {
    switch (tool) {
        case 'kicad':
            return (0, kicadValidation_1.detectKicadProjects)(files, fileDetails);
        case 'altium':
            return (0, altiumValidation_1.detectAltiumProjects)(files, fileDetails);
        case 'eagle':
            return (0, eagleValidation_1.detectEagleProjects)(files, fileDetails);
        case 'orcad':
            return (0, orcadValidation_1.detectOrcadProjects)(files, fileDetails);
        case 'allegro':
            return (0, allegroValidation_1.detectAllegroProjects)(files, fileDetails);
        case 'xpedition':
            return (0, xpeditionValidation_1.detectXpeditionProjects)(files, fileDetails);
        default:
            return { projects: [], warnings: [] };
    }
}
//# sourceMappingURL=validation.js.map